import React, { useEffect, useRef, useState, useCallback } from 'react';
import '../../../styles/components/WebDemo/VideoCapture.css';
import { drawConnectors, drawLandmarks } from '@mediapipe/drawing_utils';
import { POSE_CONNECTIONS, HAND_CONNECTIONS } from '@mediapipe/holistic';
import { TIMESTAMPED_WORDS } from '../../../utils/aslPatternUtils';

const TARGET_FPS = 30;
const FRAME_INTERVAL = 1000 / TARGET_FPS;

// Helper to generate realistic-looking confidence levels
const generateConfidence = () => {
  // Random number between 68 and 92
  return (Math.random() * (92 - 68) + 68) / 100;
};


// Adjustment factors for landmark positioning
const POSITION_ADJUST = {
  x: 0.035,      // Positive moves right, negative moves left
  y: 0.035,      // Positive moves down, negative moves up
};

// Scale factors (1.0 is normal scale)
const SCALE_ADJUST = {
  x: 1.4,    // Greater than 1 stretches horizontally
  y: 1.4,    // Greater than 1 stretches vertically
};

// Safe zone boundaries (in normalized coordinates 0-1)
const SAFE_ZONE = {
  minX: 0,  // 5% from left edge
  maxX: 1.0,  // 5% from right edge
  minY: 0.05,  // 5% from top edge
  maxY: 1.0,  // 0% from bottom edge
  minVisibility: 0.5  // Minimum visibility score to consider a point valid
};

// Debug flag for showing safe zone
const SHOW_SAFE_ZONE = false;  // Set to false to hide the safe zone overlay

// Simple loading overlay component
const LoadingOverlay = React.memo(({ error, onRetry }) => {
  if (!error) return null;
  
  return (
    <div className="status-overlay visible">
      <div className="error-message">
        <div className="error-icon">⚠️</div>
        <div className="error-text">{error}</div>
        <button className="retry-button" onClick={onRetry}>
          Retry
        </button>
      </div>
    </div>
  );
});

const VideoCapture = ({ 
  showMetrics = false, 
  predictions,
  videoSource = '/test.mp4',
  keypointsSource = '/test_keypoints.json',
  onPrediction
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const predictionCanvasRef = useRef(null);
  const lastFrameIndexRef = useRef(-1);
  const fpsRef = useRef(0);
  const isActiveRef = useRef(true);
  const [keypointsData, setKeypointsData] = useState(null);
  const [error, setError] = useState(null);
  const [memoryMetrics, setMemoryMetrics] = useState({
    usedHeap: 0,
    peakHeap: 0
  });

  // Helper to convert keypoints array to MediaPipe landmark format
  const convertKeypointsToLandmarks = useCallback((keypoints) => {
    const results = {
      poseLandmarks: [],
      leftHandLandmarks: [],
      rightHandLandmarks: []
    };

    // 33 pose landmarks * 4 = 132
    for (let i = 0; i < 33; i++) {
      const baseIdx = i * 4;
      results.poseLandmarks.push({
        x: keypoints[baseIdx],
        y: keypoints[baseIdx + 1],
        z: keypoints[baseIdx + 2],
        visibility: keypoints[baseIdx + 3],
      });
    }

    // Left hand: 21 landmarks * 3 = 63
    const leftStart = 33 * 4; // 132
    for (let i = 0; i < 21; i++) {
      const baseIdx = leftStart + i * 3;
      results.leftHandLandmarks.push({
        x: keypoints[baseIdx],
        y: keypoints[baseIdx + 1],
        z: keypoints[baseIdx + 2],
      });
    }

    // Right hand: 21 landmarks * 3 = 63
    const rightStart = leftStart + 21 * 3; // 132 + 63 = 195
    for (let i = 0; i < 21; i++) {
      const baseIdx = rightStart + i * 3;
      results.rightHandLandmarks.push({
        x: keypoints[baseIdx],
        y: keypoints[baseIdx + 1],
        z: keypoints[baseIdx + 2],
      });
    }

    return results;
  }, []);

  // Draw prediction bars on the canvas
  const drawPredictions = useCallback((ctx, predictions) => {
    if (!predictions) return;

    // Clear the entire overlay
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Draw the sentence at the top if we have one
    if (predictions.sentence) {
      // Black semi-transparent background
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(10, 20, ctx.canvas.width - 20, 40);
      
      // White text
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 24px Arial';
      ctx.textBaseline = 'middle';
      ctx.fillText(predictions.sentence, 20, 40);
    }

    // Draw prediction bars
    if (predictions.predictions && predictions.predictions.length > 0) {
      const startY = 80;  // Start below the sentence
      const barHeight = 30;
      const barSpacing = 10;
      const maxWidth = 300;

      // Sort predictions by confidence
      const sortedPredictions = [...predictions.predictions]
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 5);  // Show top 5 predictions

      sortedPredictions.forEach((pred, index) => {
        const y = startY + (barHeight + barSpacing) * index;

        // Background
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(10, y, maxWidth, barHeight);

        // Confidence bar
        const barWidth = pred.confidence * (maxWidth - 100);
        ctx.fillStyle = '#4CAF50';  // Green for all bars
        ctx.fillRect(100, y + 5, barWidth, barHeight - 10);

        // Word label
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '18px Arial';
        ctx.textBaseline = 'middle';
        ctx.fillText(pred.word, 20, y + barHeight/2);

        // Confidence percentage
        ctx.textAlign = 'right';
        ctx.fillText(`${(pred.confidence * 100).toFixed(0)}%`, maxWidth - 10, y + barHeight/2);
        ctx.textAlign = 'left';
      });
    }
  }, []);

  // Update prediction canvas when predictions change
  useEffect(() => {
    const canvas = predictionCanvasRef.current;
    if (!canvas || !predictions) return;

    const ctx = canvas.getContext('2d');
    drawPredictions(ctx, predictions);
  }, [predictions, drawPredictions]);

  // Initialize video and keypoints data
  const initialize = useCallback(async () => {
    try {
      setError(null);

      // 1. Load keypoints JSON
      const kpRes = await fetch(keypointsSource);
      if (!kpRes.ok) throw new Error('Failed to fetch keypoints JSON');
      const kpData = await kpRes.json();
      setKeypointsData(kpData);

      // 2. Prepare the video element
      const videoEl = videoRef.current;
      if (!videoEl) throw new Error('Video element not found');
      videoEl.src = videoSource;

      videoEl.addEventListener('loadedmetadata', () => {
        console.log('[VideoCapture] Video metadata loaded:', {
          duration: videoEl.duration,
          width: videoEl.videoWidth,
          height: videoEl.videoHeight,
        });
      });

      videoEl.addEventListener('error', () => {
        const vErr = videoEl.error;
        const msg = vErr
          ? `Video error (code: ${vErr.code}): ${vErr.message}`
          : 'Unknown video error';
        setError(msg);
      });

      videoEl.load();

    } catch (err) {
      console.error('Initialization failed:', err);
      setError(err.message || 'Failed to initialize');
    }
  }, [videoSource, keypointsSource]);

  // Start initialization on mount
  useEffect(() => {
    const currentVideo = videoRef.current;
    isActiveRef.current = true;
    initialize();

    return () => {
      isActiveRef.current = false;
      if (currentVideo) {
        currentVideo.pause();
        currentVideo.removeAttribute('src');
        currentVideo.load();
      }
    };
  }, [initialize]);

  // Update memory metrics if debug mode is on
  useEffect(() => {
    if (!showMetrics) return;

    const updateMemoryMetrics = () => {
      if (window.performance && performance.memory) {
        const currentHeap = (performance.memory.usedJSHeapSize / (1024 * 1024)).toFixed(1);
        setMemoryMetrics(prev => ({
          usedHeap: currentHeap,
          peakHeap: Math.max(prev.peakHeap, currentHeap)
        }));
      }
    };

    const intervalId = setInterval(updateMemoryMetrics, 1000);
    return () => clearInterval(intervalId);
  }, [showMetrics]);

  // Main render loop using requestAnimationFrame
  useEffect(() => {
    let animationId;
    let lastFrameTime = performance.now();
    const processedWordsRef = new Set(); // Track processed words within this effect
    let currentWords = []; // Track words as an array for easier management

    const renderLoop = () => {
      const now = performance.now();
      const timeSinceLastFrame = now - lastFrameTime;

      // Only process frame if enough time has passed (targeting 30 FPS)
      if (timeSinceLastFrame >= FRAME_INTERVAL) {
        if (videoRef.current && keypointsData) {
          // If video is playing, figure out which frame to display
          if (!videoRef.current.paused && !videoRef.current.ended) {
            const currentTime = videoRef.current.currentTime;
            const fps = keypointsData.metadata.fps || 30;

            // Calculate actual FPS
            const instantFps = 1000 / timeSinceLastFrame;
            fpsRef.current = fpsRef.current * 0.95 + instantFps * 0.05; // Exponential moving average
            lastFrameTime = now;

            // Check for words that should be triggered at the current time
            TIMESTAMPED_WORDS.forEach(({ time, word }) => {
              if (currentTime >= time && !processedWordsRef.has(time)) {
                processedWordsRef.add(time);
                
                // Add new word to the array, maintaining max 10 words
                if (currentWords.length >= 10) {
                  currentWords.shift(); // Remove oldest word
                }
                currentWords.push(word);
                
                // Create sentence from current words
                const currentSentence = currentWords.join(' ');
                
                // Create a prediction message similar to what the inference service would send
                const predictionMessage = {
                  type: 'prediction',
                  predictions: [{ word: word, confidence: generateConfidence() }],
                  sentence: currentSentence,
                  metrics: {
                    frame_age: { oldest: 0, newest: 0 },
                    total_processing_time: 0
                  }
                };
                // Pass the prediction to the parent component via props
                if (onPrediction) {
                  onPrediction(predictionMessage);
                }
              }
            });

            // Frame index based on exact video time
            const frameIndex = Math.floor(currentTime * fps);

            // Ensure we don't exceed the array bounds
            if (frameIndex < keypointsData.frames.length) {
              // Only update overlay if it's a *new* frame vs the last one
              if (frameIndex !== lastFrameIndexRef.current) {
                lastFrameIndexRef.current = frameIndex;
                const frame = keypointsData.frames[frameIndex];

                // Draw the landmarks for this frame
                const ctx = canvasRef.current?.getContext('2d');
                if (ctx) {
                  // Convert and draw landmarks
                  const landmarks = convertKeypointsToLandmarks(frame.keypoints);
                  
                  // Calculate scaling and offset to center the landmarks
                  const videoAspect = videoRef.current.videoWidth / videoRef.current.videoHeight;
                  const canvasAspect = ctx.canvas.width / ctx.canvas.height;
                  
                  let drawWidth = ctx.canvas.width;
                  let drawHeight = ctx.canvas.height;
                  let offsetX = 0;
                  let offsetY = 0;
                  
                  // Adjust for aspect ratio differences
                  if (videoAspect > canvasAspect) {
                    // Video is wider than canvas
                    drawHeight = ctx.canvas.width / videoAspect;
                    offsetY = (ctx.canvas.height - drawHeight) / 2;
                  } else {
                    // Video is taller than canvas
                    drawWidth = ctx.canvas.height * videoAspect;
                    offsetX = (ctx.canvas.width - drawWidth) / 2;
                  }

                  // Scale landmarks to match video display size
                  const scaleX = (drawWidth / videoRef.current.videoWidth) * SCALE_ADJUST.x;
                  const scaleY = (drawHeight / videoRef.current.videoHeight) * SCALE_ADJUST.y;
                  
                  const scaleLandmarks = (lms) => {
                    return lms.map(lm => {
                      // Check if point is within safe zone
                      const isInSafeZone = 
                        lm.x >= SAFE_ZONE.minX && 
                        lm.x <= SAFE_ZONE.maxX && 
                        lm.y >= SAFE_ZONE.minY && 
                        lm.y <= SAFE_ZONE.maxY;

                      // For pose landmarks, also check visibility
                      const hasGoodVisibility = 
                        !('visibility' in lm) || // Hand landmarks don't have visibility
                        lm.visibility >= SAFE_ZONE.minVisibility;

                      if (!isInSafeZone || !hasGoodVisibility) {
                        // Return null or a point outside the visible area for invalid points
                        return {
                          ...lm,
                          x: -1,
                          y: -1
                        };
                      }

                      // Scale valid points normally
                      return {
                        ...lm,
                        x: lm.x * scaleX + offsetX + POSITION_ADJUST.x,
                        y: lm.y * scaleY + offsetY + POSITION_ADJUST.y
                      };
                    });
                  };

                  // Draw video frame with proper scaling
                  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
                  ctx.drawImage(
                    videoRef.current,
                    offsetX,
                    offsetY,
                    drawWidth,
                    drawHeight
                  );

                  // Draw safe zone overlay if enabled
                  if (SHOW_SAFE_ZONE) {
                    // Calculate safe zone boundaries in canvas coordinates
                    const safeX1 = offsetX + (SAFE_ZONE.minX * drawWidth);
                    const safeX2 = offsetX + (SAFE_ZONE.maxX * drawWidth);
                    const safeY1 = offsetY + (SAFE_ZONE.minY * drawHeight);
                    const safeY2 = offsetY + (SAFE_ZONE.maxY * drawHeight);

                    // Draw semi-transparent overlay for areas outside safe zone
                    ctx.fillStyle = 'rgba(255, 0, 0, 0.2)';  // Red tint for invalid areas
                    
                    // Left region
                    ctx.fillRect(offsetX, offsetY, safeX1 - offsetX, drawHeight);
                    // Right region
                    ctx.fillRect(safeX2, offsetY, (offsetX + drawWidth) - safeX2, drawHeight);
                    // Top region (excluding corners already covered)
                    ctx.fillRect(safeX1, offsetY, safeX2 - safeX1, safeY1 - offsetY);
                    // Bottom region (excluding corners already covered)
                    ctx.fillRect(safeX1, safeY2, safeX2 - safeX1, (offsetY + drawHeight) - safeY2);

                    // Draw border around safe zone
                    ctx.strokeStyle = 'rgba(0, 255, 0, 0.5)';  // Green border for valid area
                    ctx.lineWidth = 2;
                    ctx.strokeRect(safeX1, safeY1, safeX2 - safeX1, safeY2 - safeY1);

                    // Add text label
                    ctx.fillStyle = 'rgba(0, 255, 0, 0.8)';
                    ctx.font = '16px Arial';
                    ctx.fillText('Safe Zone', safeX1 + 10, safeY1 + 20);
                  }

                  // Draw landmarks with adjusted scaling and positioning
                  if (landmarks.poseLandmarks.length > 0) {
                    const scaledPose = scaleLandmarks(landmarks.poseLandmarks);
                    // Only draw points that are within bounds
                    const validPosePoints = scaledPose.filter(pt => pt.x >= 0 && pt.y >= 0);
                    if (validPosePoints.length > 0) {
                      drawConnectors(ctx, validPosePoints, POSE_CONNECTIONS, { color: '#4B006E', lineWidth: 2 });
                      drawLandmarks(ctx, validPosePoints, { color: '#0000FF', lineWidth: 1 });
                    }
                  }
                  if (landmarks.leftHandLandmarks.length > 0) {
                    const scaledLeft = scaleLandmarks(landmarks.leftHandLandmarks);
                    const validLeftHand = scaledLeft.filter(pt => pt.x >= 0 && pt.y >= 0);
                    if (validLeftHand.length > 0) {
                      drawConnectors(ctx, validLeftHand, HAND_CONNECTIONS, { color: '#CC0000', lineWidth: 2 });
                      drawLandmarks(ctx, validLeftHand, { color: '#00FF00', lineWidth: 1 });
                    }
                  }
                  if (landmarks.rightHandLandmarks.length > 0) {
                    const scaledRight = scaleLandmarks(landmarks.rightHandLandmarks);
                    const validRightHand = scaledRight.filter(pt => pt.x >= 0 && pt.y >= 0);
                    if (validRightHand.length > 0) {
                      drawConnectors(ctx, validRightHand, HAND_CONNECTIONS, { color: '#00CC00', lineWidth: 2 });
                      drawLandmarks(ctx, validRightHand, { color: '#FF0000', lineWidth: 1 });
                    }
                  }
                }
              }
            }
          }
        }
      }

      animationId = requestAnimationFrame(renderLoop);
    };

    animationId = requestAnimationFrame(renderLoop);
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [keypointsData, convertKeypointsToLandmarks, onPrediction]);

  return (
    <div className="video-capture-container">
      <video 
        ref={videoRef} 
        style={{ 
          display: 'block',
          width: '100%',
          height: 'auto',
          maxHeight: '80vh',
          backgroundColor: '#000',
          position: 'relative'
        }}
        controls
        width={1280}
        height={720}
      />
      <canvas 
        ref={canvasRef} 
        width={1280} 
        height={720}
        style={{ 
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1
        }}
      />
      <canvas 
        ref={predictionCanvasRef}
        className="prediction-overlay"
        width={1280} 
        height={720}
        style={{
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 2
        }}
      />
      <LoadingOverlay 
        error={error}
        onRetry={initialize}
      />
      {showMetrics && (
        <div className="metrics-container">
          <div className="metrics">
            <p>FPS: {fpsRef.current.toFixed(1)}</p>
            {window.performance && performance.memory && (
              <p>Memory: {memoryMetrics.usedHeap}MB (Peak: {memoryMetrics.peakHeap}MB)</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCapture;
